/**
 * @file Frontend `Www` — typed Client for the marketing site SPA.
 * @module @labcraft/www/src/client
 *
 * Single `CoreClient` export of `@labcraft/www/src/client` per the
 * rule in `.agents/rules/services.md` § "Workspace Client shape —
 * one `CoreClient`, lifecycle namespacers". `Www` adopts the shared
 * `HttpTransport` at construction; the lifecycle child-walk drives
 * configure / start / stop.
 *
 * Two `CoreLifecycle` namespacers, one per nav-menu item with a
 * wire surface:
 *
 *   - {@link Account} — `www.account.*`, AccountMenu in TopNav +
 *     `/authorize` route family.
 *   - {@link Contact} — `www.contact.*`, the `/contact` top-nav item
 *     (CAPTCHA mint + form submit).
 *
 * `main.tsx` owns the 401-bounce wiring: it calls
 * `http.on('unauthorized', ...)` with a callback that flips the
 * `qkMe` TanStack entry to `{ learner: null }`, then constructs `Www`
 * with the same transport. This Client doesn't depend on
 * `@tanstack/react-query` — it's vanilla wire surface, SW-ready.
 *
 * Shares its class name with the backend `Www` in
 * `../service/index.ts` and the subprocess fixture `Www` in
 * `../../test/service.ts` by design — the three never appear in the
 * same module, per ADR-010's package-name-as-class-name convention.
 */

import { CoreClient, type CoreTransport } from '@labcraft/core'
import type { HttpClientTransport } from '@labcraft/core/frontend'
import { ConfigRoute, type WwwConfigResponse } from '../shared/routes.ts'
import { Account } from './account.ts'
import { Contact } from './contact.ts'

////////////////////////////////////////////////////////////////////////////////
// Transport seam
////////////////////////////////////////////////////////////////////////////////

/**
 * Structural transport contract every www namespacer (and the parent)
 * consumes — the `request` + `stream` + `on('unauthorized', ...)` subset of
 * `HttpClientTransport`, intersected with `CoreTransport` so the
 * parent can carry it as a `CoreClient` transport-record value.
 *
 * Both `HttpClientTransport` and `VirtualHostTransport` extend
 * `CoreTransport` and expose the same three methods, so the integration
 * harness constructs `Www` with `VirtualHostTransport` unchanged.
 */
export type HttpTransport = CoreTransport &
  Pick<HttpClientTransport, 'request' | 'stream'>

////////////////////////////////////////////////////////////////////////////////
// Props + Deps
////////////////////////////////////////////////////////////////////////////////

/**
 * Constructor props for the frontend `Www`. No knobs beyond the
 * lifecycle id.
 *
 * @property id - CoreLifecycle id (default `'www'`).
 */
export type WwwProps = {
  id?: string
}

/**
 * Runtime dependencies. The operator (`main.tsx` for production
 * mounts; the integration harness for tests) constructs the transport
 * once and threads it through.
 *
 * @property http - Typed HTTP transport. Stateless; one instance per
 *   page load.
 */
export type WwwDeps = {
  http: HttpTransport
}

////////////////////////////////////////////////////////////////////////////////
// Www (frontend Client)
////////////////////////////////////////////////////////////////////////////////

/**
 * Top-level frontend Client for `www.labcraft.dev`. Single `CoreClient`
 * export of `@labcraft/www/src/client`.
 */
export class Www extends CoreClient<WwwProps, { http: HttpTransport }> {
  readonly account: Account
  readonly contact: Contact

  constructor(props: WwwProps, deps: WwwDeps) {
    super({ id: props.id ?? 'www' }, { transports: { http: deps.http } })
    const http = this.deps.transports.http
    this.account = this.adopt(new Account(http))
    this.contact = this.adopt(new Contact(http))
  }

  /**
   * `GET /api/config` — runtime config for the SPA bootstrap
   * (waitlist mode + lab origin). Shell-level concern, so it's a flat
   * method on the parent rather than a nav-style namespacer. The
   * returned `waitlistEnabled` drives copy only; access is enforced
   * server-side (see {@link WwwConfigResponse}).
   */
  async config(): Promise<WwwConfigResponse> {
    return this.deps.transports.http.request(ConfigRoute)
  }
}
